import React from 'react';
import { Form, Checkbox, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';

// A start and end time per weekday (the app's time zone), as stored by
// routes/weekly_hours.py: { Mon: { open: '08:00', close: '17:00' }, ... }.
// "All day" is 00:00 to 23:59. Used for Help Desk queue operating hours and
// Teams' non on-call hours.

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ALL_DAY = { open: '00:00', close: '23:59' };
const t = (hhmm) => dayjs(`2000-01-01T${hhmm}`);
const fmt12 = (hhmm) => t(hhmm).format('h:mm A');
const isAllDay = (span) => span?.open === ALL_DAY.open && span?.close === ALL_DAY.close;

// Stored hours -> form values for the editor.
export function hoursToForm(hours) {
  const h = hours || {};
  return Object.fromEntries(WEEKDAYS.map((d) => [d, {
    on: !!h[d],
    allDay: isAllDay(h[d]),
    range: h[d] && !isAllDay(h[d]) ? [t(h[d].open), t(h[d].close)] : null,
  }]));
}

// Form values -> { hours, error }. hours is null when no day is ticked.
export function formToHours(values) {
  const out = {};
  for (const d of WEEKDAYS) {
    const v = values?.[d];
    if (!v?.on) continue;
    if (v.allDay) { out[d] = { ...ALL_DAY }; continue; }
    if (!v.range?.[0] || !v.range?.[1]) return { hours: null, error: `${d}: pick the start and end times.` };
    out[d] = { open: v.range[0].format('HH:mm'), close: v.range[1].format('HH:mm') };
  }
  return { hours: Object.keys(out).length ? out : null, error: null };
}

// "Mon-Fri 8:00 AM-5:00 PM", "Sat All day": consecutive days alike run together.
export function hoursSummary(hours) {
  if (!hours) return null;
  const label = (s) => (isAllDay(s) ? 'All day' : `${fmt12(s.open)}-${fmt12(s.close)}`);
  const parts = [];
  let i = 0;
  while (i < WEEKDAYS.length) {
    const span = hours[WEEKDAYS[i]];
    if (!span) { i += 1; continue; }
    let j = i;
    while (j + 1 < WEEKDAYS.length && hours[WEEKDAYS[j + 1]]
      && label(hours[WEEKDAYS[j + 1]]) === label(span)) j += 1;
    parts.push(`${i === j ? WEEKDAYS[i] : `${WEEKDAYS[i]}-${WEEKDAYS[j]}`} ${label(span)}`);
    i = j + 1;
  }
  return parts;
}

// The rows, inside a parent <Form>; values live under `name` (e.g. 'hours').
// offLabel is what an unticked day reads as (e.g. 'Closed').
export default function WeeklyHoursEditor({ name, offLabel = 'None' }) {
  return (
    <>
      {WEEKDAYS.map((d) => (
        <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
          <Form.Item name={[name, d, 'on']} valuePropName="checked" noStyle>
            <Checkbox style={{ width: 70 }}>{d}</Checkbox>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a[name]?.[d]?.on !== b[name]?.[d]?.on
            || a[name]?.[d]?.allDay !== b[name]?.[d]?.allDay}>
            {({ getFieldValue }) => {
              if (!getFieldValue([name, d, 'on'])) return <Typography.Text type="secondary">{offLabel}</Typography.Text>;
              const allDay = getFieldValue([name, d, 'allDay']);
              return (
                <>
                  {!allDay && (
                    <Form.Item name={[name, d, 'range']} noStyle>
                      <TimePicker.RangePicker format="h:mm A" use12Hours minuteStep={15} order />
                    </Form.Item>
                  )}
                  <Form.Item name={[name, d, 'allDay']} valuePropName="checked" noStyle>
                    <Checkbox>All day</Checkbox>
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
        </div>
      ))}
    </>
  );
}
